import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import {
  AppDatePicker,
  AppFileUploader,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import Translator from '../../../../../components/helpers/Translator';
import { axiosPath } from '../../../../../../routing/helpers/router';
import minions from '../../../../../../minions/minions';
import errorHandler from '../../../../../components/error/ErrorHandler';
import AppFileUploaderList from '../../../../../components/form/File/AppFileUploaderList';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import { handleDisabled, handleMinDate } from '../../tools/comsaReportsTools';

export default function ModuleComsaAtmosphericEmissionsCorporateFleetReportUpdate(
  props,
) {
  const { report, onRechargeReports, moduleData } = props;
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [showModalReport, setShowModalReport] = useState(false);
  const type = 'atmosphericEmissionsCorporateFleet';
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator file="user" name={`calendar.month${i + 1}`} />,
      value: i + 1,
    })),
  );
  const validationSchema = Yup.object().shape({
    year: Yup.date()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="m-cr-required" />,
        ),
      ),
    month: Yup.date()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="m-cr-required" />,
        ),
      ),
    total: Yup.string()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="m-cr-required" />,
        ),
      ),
  });

  const handleUpdateReport = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath('api_r_user_module_comsaReports_editReport'),
          minions.forms.toFormData({
            ...payload,
            id: report.id,
            type: type,
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        .then(() => {
          if (isMounted.current) onRechargeReports();
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setShowModalReport(false)),
    [moduleBridgeHex, subModuleBridgeHex, plantHex],
  );

  return (
    <>
      <button
        type="button"
        className="btn btn-info btn-xs mr-1"
        onClick={() => setShowModalReport(true)}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="edit" />,
        )}
        disabled={handleDisabled(moduleData, report?.year)}
      >
        <AppRemixIcon icon="edit" />
      </button>

      <AppModal
        show={showModalReport}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-comsaReports"
            name="m-cr-update.atmospheric-emissions-corporate-fleet"
          />,
        )}
        size="lg"
        onClose={() => setShowModalReport(false)}
      >
        <Formik
          initialValues={{
            year: report.year,
            month: Number(report.month),
            total: report.total,
            comment: report.comment,
            files: [],
            deletedFiles: [],
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleUpdateReport}
        >
          {({ setFieldValue, handleSubmit, values, errors }) => (
            <>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppDatePicker
                      title={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="year" />,
                      )}
                      value={values.year}
                      onChange={(opt) => setFieldValue('year', opt)}
                      minDate={handleMinDate(moduleData)}
                      dateFormat="yyyy"
                      displayDateFormat="YYYY"
                      placeholder={
                        <Translator file="module-comsaReports" name="select.a.year" />
                      }
                      isClearable
                      showYearPicker
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      error={errors.year}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="mb-3">
                    <AppSingleSelect
                      title={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="month" />,
                      )}
                      options={months}
                      onChange={(opt) => setFieldValue('month', opt)}
                      icon={<AppRemixIcon icon="calendar" />}
                      placeholder={
                        <Translator
                          file="module-comsaReports"
                          name="select.a.month"
                        />
                      }
                      value={values.month}
                      error={errors.month}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <AppInputNumber
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="m-cr-quantity" />,
                    )}
                    icon={<AppRemixIcon icon="scales" />}
                    value={values.total}
                    error={errors.total}
                    onChange={(value) => setFieldValue('total', value)}
                    placeholder={
                      <Translator file="module-comsaReports" name="m-cr-quantity" />
                    }
                    decimals={3}
                    append={
                      <div className="input-group-append">
                        <span className="input-group-text">
                          <Translator file="module-comsaReports" name="m-cr-tons" />
                        </span>
                      </div>
                    }
                    required
                  />
                  {errors.total && <div className="text-danger">*{errors.total}</div>}
                </div>
                <div className="col-12">
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="m-cr-comment" />,
                    )}
                    value={values?.comment}
                    onChange={(v) => setFieldValue(`comment`, v)}
                    height="89px"
                  />
                </div>
              </div>

              <div>
                <div className="text-primary mt-3">
                  <Translator file="module-comsaReports" name="attach.evidence" />
                </div>

                <div className="row mb-3">
                  <div className="col-sm-12 col-lg-6">
                    <AppFileUploader
                      isMulti
                      placeholder={
                        !values.files || Object.keys(values?.files).length === 0
                          ? ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="select.one.multiple.files"
                              />,
                            )
                          : ReactDOMServer.renderToString(
                              <Translator
                                file="general"
                                name="select.number.of.elements"
                                variables={{
                                  elements: Object.keys(values?.files).length,
                                }}
                              />,
                            )
                      }
                      onChange={(opt) => setFieldValue('files', opt)}
                      btnText={<Translator file="general" name="search..." />}
                    />
                  </div>

                  <div className="col-sm-12 col-lg-6">
                    <AppFileUploaderList
                      values={values}
                      setFieldValue={setFieldValue}
                      urlsAvaliable={report.files}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <button
                    className="btn btn-success btn-block"
                    type="submit"
                    onClick={handleSubmit}
                  >
                    <Translator file="module-comsaReports" name="edit" />
                  </button>
                </div>
              </div>
            </>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
